import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import Button from '@mui/material/Button'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import PolicyIcon from '@mui/icons-material/Policy'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import EditIcon from '@mui/icons-material/Edit'
import LaunchIcon from '@mui/icons-material/Launch'

import { BootstrapDialog, BootstrapDialogTitle } from '../shared/BootstrapDialog'
import { validateField, validateOptionalField, descriptionLimit, validateRequiredResourceTags, collectTagData } from '../../utils/formValidation'
import ResourceTagsSection from '../shared/ResourceTagsSection'
import ResourceCheckboxes from '../shared/ResourceCheckboxes'
import TCAIREMFieldsSection from '../shared/TCAIREMFieldsSection'
import ResourceDescriptionField from '../shared/ResourceDescriptionField'
import { AdminPolicyModalStyle } from '../styles'

import { setErrorNotification } from '../../reducers/notificationReducer'
import { deletePolicy, updatePolicy } from '../../reducers/policiesReducer'
import { openExternal } from '../../utils/safeUrl'

const AdminPolicyModal = (props) => {
    const user = useSelector(state => state.user)
    const dispatch = useDispatch()

    // form state (no need to use redux for form state)
    // eslint-disable-next-line no-unused-vars
    const [name, setName] = useState('')
    // eslint-disable-next-line no-unused-vars
    const [nameError, setNameError] = useState(false)
    // eslint-disable-next-line no-unused-vars
    const [nameHelper, setNameHelper] = useState('')

    const [title, setTitle] = useState('')
    const [titleError, setTitleError] = useState(false)
    const [titleHelper, setTitleHelper] = useState('')

    const [country, setCountry] = useState('')
    const [countryError, setCountryError] = useState(false)
    const [countryHelper, setCountryHelper] = useState('')

    const [institution, setInstitution] = useState('')
    const [institutionError, setInstitutionError] = useState(false)
    const [institutionHelper, setInstitutionHelper] = useState('')

    const [created, setCreated] = useState('')
    const [createdError, setCreatedError] = useState(false)
    const [createdHelper, setCreatedHelper] = useState('')

    const [link, setLink] = useState('')
    const [linkError, setLinkError] = useState(false)
    const [linkHelper, setLinkHelper] = useState('')

    const [approved, setApproved] = useState('')
    // eslint-disable-next-line no-unused-vars
    const [approvedError, setApprovedError] = useState(false)
    // eslint-disable-next-line no-unused-vars
    const [approvedHelper, setApprovedHelper] = useState('')

    const [updated, setUpdated] = useState('')
    const [updatedError, setUpdatedError] = useState(false)
    const [updatedHelper, setUpdatedHelper] = useState('')

    const [enacted, setEnacted] = useState('')
    const [enactedError, setEnactedError] = useState(false)
    const [enactedHelper, setEnactedHelper] = useState('')

    const [published, setPublished] = useState('')
    const [publishedError, setPublishedError] = useState(false)
    const [publishedHelper, setPublishedHelper] = useState('')

    const [description, setDescription] = useState('')
    const [descriptionError, setDescriptionError] = useState(false)
    const [descriptionHelper, setDescriptionHelper] = useState('')

    const [contentTags, setContentTags] = useState([])
    const [medicalTags, setMedicalTags] = useState([])
    const [adminCategoryTags, setAdminCategoryTags] = useState([])
    const [experienceTags, setExperienceTags] = useState([])
    const [demographicTags, setDemographicTags] = useState([])
    const [showTagErrors, setShowTagErrors] = useState(false)
    const [isSeminalWork, setIsSeminalWork] = useState(false)
    const [inQuiz, setInQuiz] = useState(false)
    const [isTcairem, setIsTcairem] = useState(false)
    const [isPaid, setIsPaid] = useState(false)
    const [tcairemTags, setTcairemTags] = useState([])
    const [personaTags, setPersonaTags] = useState([])

    // init fields
    useEffect(() => {
        setTitle(props.title)
        setCountry(props.country)
        setInstitution(props.institution)
        setCreated(props.created)
        setLink(props.link)
        setApproved(props.approved)
        setUpdated(props.updated)
        setEnacted(props.enacted)
        setPublished(props.published)
        setDescription(props.description || '')
        setContentTags((props.content_tags || props.tags || []).map(String))
        setMedicalTags((props.medical_specialty_tags || []).map(String))
        setAdminCategoryTags((props.admin_category_tags || []).map(String))
        setExperienceTags((props.experience_tags || []).map(String))
        setDemographicTags((props.demographic_tags || []).map(String))
        setIsSeminalWork(props.is_seminal_work || false)
        setInQuiz(props.in_quiz || false)
        setIsTcairem(props.is_tcairem || false)
        setIsPaid(props.is_paid || false)
        setTcairemTags((props.tcairem_type_tags || []).map(String))
        setPersonaTags((props.tcairem_persona_tags || []).map(String))
    }, [props.title, props.country, props.institution, props.created, props.link, props.approved, props.updated, props.enacted, props.published, props.description, props.content_tags, props.medical_specialty_tags, props.admin_category_tags, props.experience_tags, props.demographic_tags, props.tags, props.is_seminal_work, props.in_quiz, props.is_tcairem, props.is_paid, props.tcairem_type_tags, props.tcairem_persona_tags])

    // reset fields to original values if user wants to
    const reset = () => {
        setTitle(props.title)
        setCountry(props.country)
        setInstitution(props.institution)
        setCreated(props.created)
        setLink(props.link)
        setApproved(props.approved)
        setUpdated(props.updated)
        setEnacted(props.enacted)
        setPublished(props.published)
        setDescription(props.description || '')
        setContentTags((props.content_tags || props.tags || []).map(String))
        setMedicalTags((props.medical_specialty_tags || []).map(String))
        setAdminCategoryTags((props.admin_category_tags || []).map(String))
        setExperienceTags((props.experience_tags || []).map(String))
        setDemographicTags((props.demographic_tags || []).map(String))
        setIsSeminalWork(props.is_seminal_work || false)
        setInQuiz(props.in_quiz || false)
        setIsTcairem(props.is_tcairem || false)
        setIsPaid(props.is_paid || false)
        setTcairemTags((props.tcairem_type_tags || []).map(String))
        setPersonaTags((props.tcairem_persona_tags || []).map(String))

        setTitleError(false)

        setTitleHelper('')
        setCountryError(false)
        setCountryHelper('')
        setInstitutionError(false)
        setInstitutionHelper('')
        setCreatedError(false)
        setCreatedHelper('')
        setLinkError(false)
        setLinkHelper('')
        setApprovedError(false)
        setApprovedHelper('')
        setUpdatedError(false)
        setUpdatedHelper('')
        setEnactedError(false)
        setEnactedHelper('')
        setPublishedError(false)
        setPublishedHelper('')
        setDescriptionError(false)
        setDescriptionHelper('')
        setShowTagErrors(false)
    }

    // delete resource
    const handleReject = async () => {
        if (window.confirm('Are you sure? Note this resource cannot be retrieved after rejection.')) {
            handleClose()
            await dispatch(deletePolicy(props.id, user.staff))
            props.onChanged?.()
        }
    }

    // if user clicks approve/disapprove all I need to do is toggle approved state and update fields
    const handleSubmit = async () => {
        setShowTagErrors(true)
        if (
            validateField(title, setTitleError, setTitleHelper) &&
            validateField(country, setCountryError, setCountryHelper) &&
            validateField(institution, setInstitutionError, setInstitutionHelper) &&
            validateField(created, setCreatedError, setCreatedHelper) &&
            validateField(link, setLinkError, setLinkHelper) &&
            validateField(approved, setApprovedError, setApprovedHelper) &&
            validateField(updated, setUpdatedError, setUpdatedHelper) &&
            validateField(enacted, setEnactedError, setEnactedHelper) &&
            validateField(published, setPublishedError, setPublishedHelper) &&
            validateOptionalField(description, setDescriptionError, setDescriptionHelper, descriptionLimit(props.description), 'Description') &&
            validateRequiredResourceTags({
                contentTags,
                adminCategoryTags,
                experienceTags,
                demographicTags,
                tcairemTags,
                personaTags,
                isTcairem,
            })
        ) {
            if (window.confirm('Are you sure? Note any changes you have made will be included.')) {
                const data = {
                    ...props,
                    title: title,
                    country: country,
                    institution: institution,
                    enacted: enacted,
                    published: published,
                    description: description,
                    approved: !props.approved,
                    is_seminal_work: isSeminalWork,
                    in_quiz: inQuiz,
                    is_tcairem: isTcairem,
                    is_paid: isPaid,
                    ...collectTagData({ contentTags, medicalTags, adminCategoryTags, experienceTags, demographicTags, tcairemTags, personaTags, isTcairem }),
                }

                handleClose()
                await dispatch(updatePolicy(props.id, data, user.staff))
                props.onChanged?.()
            }

        } else {
            dispatch(setErrorNotification('Please fill all required fields before submission'))
        }
    }

    // do not change approved state just update fields
    const handleSave = async () => {
        setShowTagErrors(true)
        if (
            validateField(title, setTitleError, setTitleHelper) &&
            validateField(country, setCountryError, setCountryHelper) &&
            validateField(institution, setInstitutionError, setInstitutionHelper) &&
            validateField(created, setCreatedError, setCreatedHelper) &&
            validateField(link, setLinkError, setLinkHelper) &&
            validateField(approved, setApprovedError, setApprovedHelper) &&
            validateField(updated, setUpdatedError, setUpdatedHelper) &&
            validateField(enacted, setEnactedError, setEnactedHelper) &&
            validateField(published, setPublishedError, setPublishedHelper) &&
            validateOptionalField(description, setDescriptionError, setDescriptionHelper, descriptionLimit(props.description), 'Description') &&
            validateRequiredResourceTags({
                contentTags,
                adminCategoryTags,
                experienceTags,
                demographicTags,
                tcairemTags,
                personaTags,
                isTcairem,
            })
        ) {
            const data = {
                title: title,
                country: country,
                institution: institution,
                created: created,
                link: link,
                updated: updated,
                enacted: enacted,
                published: published,
                description: description,
                is_seminal_work: isSeminalWork,
                in_quiz: inQuiz,
                is_tcairem: isTcairem,
                is_paid: isPaid,
                ...collectTagData({ contentTags, medicalTags, adminCategoryTags, experienceTags, demographicTags, tcairemTags, personaTags, isTcairem }),
            }

            const ok = await dispatch(updatePolicy(props.id, data, user.staff))
            if (ok) {
                handleClose()
                props.onChanged?.()
            }
        } else {
            dispatch(setErrorNotification('Please fill all required fields before submission'))
        }
    }

    const [open, setOpen] = useState(false)

    const handleClickOpen = () => {
        setShowTagErrors(false)
        setOpen(true)
    }

    const handleClose = () => {
        setShowTagErrors(false)
        setOpen(false)
    }

    const openResource = () => {
        openExternal(props.link)
        return false;
    }

    return (
        <div>
            <Button {...AdminPolicyModalStyle.viewButton.props} onClick={()=>handleClickOpen()} sx={AdminPolicyModalStyle.viewButton.style}>
                <EditIcon /> &nbsp; Edit
            </Button>
            <BootstrapDialog
                onClose={()=>handleClose()}
                aria-labelledby={`policy-modal-${props.id}`}
                open={open}
                fullWidth
                {...AdminPolicyModalStyle.modal}
            >
                <BootstrapDialogTitle id={`policy-modal-${props.id}`} onClose={()=>handleClose()}>
                    <Box sx={AdminPolicyModalStyle.titleContainer}>
                        <PolicyIcon sx={AdminPolicyModalStyle.icon} />
                        {props.title}
                    </Box>
                </BootstrapDialogTitle>
                    <DialogContent dividers sx={AdminPolicyModalStyle.content}>
                        <form onSubmit={handleSubmit} id={`policy-form-${props.id}`}>
                            <Typography {...AdminPolicyModalStyle.subheading} gutterBottom>
                                Manage Resource
                            </Typography>
                            {props.proposer && <Typography {...AdminPolicyModalStyle.proposerText}>
                                Created By: {props.proposer.name} ({props.proposer.email})
                            </Typography>}

                            <Grid container spacing={2} sx={AdminPolicyModalStyle.grid}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        value={title}
                                        onChange={(e)=>setTitle(e.target.value)}
                                        label='Title'
                                        required
                                        error={titleError}
                                        helperText={titleHelper}
                                        onBlur={()=>validateField(title, setTitleError, setTitleHelper)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        value={country}
                                        onChange={(e)=>setCountry(e.target.value)}
                                        label='Country'
                                        required
                                        error={countryError}
                                        helperText={countryHelper}
                                        onBlur={()=>validateField(country, setCountryError, setCountryHelper)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        value={institution}
                                        onChange={(e)=>setInstitution(e.target.value)}
                                        label='Institution'
                                        required
                                        error={institutionError}
                                        helperText={institutionHelper}
                                        onBlur={()=>validateField(institution, setInstitutionError, setInstitutionHelper)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        value={created}
                                        onChange={(e)=>setCreated(e.target.value)}
                                        label='Date Created'
                                        required
                                        error={createdError}
                                        helperText={createdHelper}
                                        onBlur={()=>validateField(created, setCreatedError, setCreatedHelper)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        value={link}
                                        onChange={(e)=>setLink(e.target.value)}
                                        label='Link'
                                        required
                                        error={linkError}
                                        helperText={linkHelper}
                                        onBlur={()=>validateField(link, setLinkError, setLinkHelper)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        value={updated}
                                        onChange={(e)=>setUpdated(e.target.value)}
                                        label='Date Updated'
                                        required
                                        error={updatedError}
                                        helperText={updatedHelper}
                                        onBlur={()=>validateField(updated, setUpdatedError, setUpdatedHelper)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        value={enacted}
                                        onChange={(e)=>setEnacted(e.target.value)}
                                        label='Date Enacted'
                                        required
                                        error={enactedError}
                                        helperText={enactedHelper}
                                        onBlur={()=>validateField(enacted, setEnactedError, setEnactedHelper)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        value={published}
                                        onChange={(e)=>setPublished(e.target.value)}
                                        label='Date Published'
                                        required
                                        error={publishedError}
                                        helperText={publishedHelper}
                                        onBlur={()=>validateField(published, setPublishedError, setPublishedHelper)}
                                    />
                                </Grid>
                                <ResourceDescriptionField
                                    value={description} setValue={setDescription}
                                    error={descriptionError} setError={setDescriptionError}
                                    helper={descriptionHelper} setHelper={setDescriptionHelper}
                                    limit={descriptionLimit(props.description)}
                                />
                                <ResourceTagsSection
                                    contentTags={contentTags} setContentTags={setContentTags}
                                    medicalTags={medicalTags} setMedicalTags={setMedicalTags}
                                    adminCategoryTags={adminCategoryTags} setAdminCategoryTags={setAdminCategoryTags}
                                    experienceTags={experienceTags} setExperienceTags={setExperienceTags}
                            demographicTags={demographicTags} setDemographicTags={setDemographicTags}
                                    required={true}
                                    forceShowRequiredErrors={showTagErrors}
                                />
                                <ResourceCheckboxes
                                    inQuiz={inQuiz} setInQuiz={setInQuiz}
                                    isSeminalWork={isSeminalWork} setIsSeminalWork={setIsSeminalWork}
                                />
                                <TCAIREMFieldsSection
                                    isTcairem={isTcairem} setIsTcairem={setIsTcairem}
                                    isPaid={isPaid} setIsPaid={setIsPaid}
                                    tcairemTags={tcairemTags} setTcairemTags={setTcairemTags}
                                    personaTags={personaTags} setPersonaTags={setPersonaTags}
                                    forceShowRequiredErrors={showTagErrors}
                                />
                            </Grid>
                            <Button {...AdminPolicyModalStyle.resetButton} type='button' onClick={() => reset()} sx={{marginTop: '20px'}}>
                                Reset Fields
                            </Button>
                        </form>
                    </DialogContent>
                    <DialogActions>
                        <Button type='button' onClick={()=>openResource()}>
                            View Resource&nbsp;<LaunchIcon {...AdminPolicyModalStyle.launch}/>
                        </Button>
                        <Button type='button' onClick={()=>handleSave()}>
                            Save Changes
                        </Button>
                        <Button type='submit' form={`policy-form-${props.id}`}>
                            {props.approved? 'Disapprove':'Approve'}
                        </Button>


                        <Button type='button' onClick={()=>handleReject()}>
                            {props.approved? 'Delete':'Reject'}
                        </Button>
                    </DialogActions>
            </BootstrapDialog>
        </div>
    )
}

export default AdminPolicyModal
